import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";
import AdaptiveLoopHeader from "@/components/adaptive-loop/AdaptiveLoopHeader";

/**
 * ApplicationTemplate — shared shell for all Application pages.
 * Applications are contexts where Adaptive Physiology is applied — not
 * separate services. Every application page follows the same structure:
 *
 *   Hero (with the current compact Adaptive Loop)
 *   The Adaptive Challenge
 *   The Traditional Gap
 *   The Adaptive Physiology Perspective
 *   Framework Application (Assess → Prescribe → Reinforce → Retrain)
 *   Desired Capacities
 *   Related Exploration
 *
 * Every page carries the four reinforcements:
 *   • The nervous system adapts.
 *   • Physiological state influences behavior.
 *   • Repeated inputs create adaptations.
 *   • Intentional inputs can create new adaptations.
 */

const DEFAULT_RELATED = [
  {
    label: "Foundational principles",
    to: "/methodology/why",
    desc: "Why Adaptive Physiology exists.",
  },
  {
    label: "The Framework",
    to: "/methodology/framework",
    desc: "The four pillars of the methodology.",
  },
  {
    label: "Scientific foundations",
    to: "/methodology/science",
    desc: "Research and physiological references.",
  },
  {
    label: "Programs",
    to: "/apply",
    desc: "How the methodology is delivered in practice.",
  },
];

const ApplicationTemplate = ({
  pageKey,
  breadcrumbLabel,
  overline,
  headline,
  headlineAccent,
  lead,
  challenge, // { title, paragraphs: [] }
  traditionalGap, // { title, paragraphs: [] }
  perspective, // { title, paragraphs: [] }
  frameworkApplication = [], // [ { title, body }, ... 4 ]
  capacities = [], // [ { icon, label, body }, ... 4-6 ]
  related = DEFAULT_RELATED,
  nextStep, // { title, description, to, cta }
}) => {
  return (
    <PageShell>
      {/* Hero */}
      <section
        data-testid={`application-${pageKey}-hero`}
        className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 border-b overflow-hidden forr-canvas"
        style={{ borderColor: "var(--forr-line-soft)" }}
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] forr-radial-teal opacity-70 pointer-events-none" />
        <div className="absolute top-40 right-[-200px] w-[500px] h-[500px] forr-radial-warm opacity-40 pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <nav
              aria-label="Breadcrumb"
              data-testid={`application-${pageKey}-breadcrumb`}
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-10"
            >
              <Link to="/" className="hover:forr-text-teal-deep transition-colors">
                Home
              </Link>
              <span className="forr-text-faint">›</span>
              <Link
                to="/applications"
                className="hover:forr-text-teal-deep transition-colors"
              >
                Applications
              </Link>
              <span className="forr-text-faint">›</span>
              <span className="forr-text-soft">{breadcrumbLabel}</span>
            </nav>
          </Reveal>

          <div className="grid grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="col-span-12 lg:col-span-7">
              <Reveal>
                <div className="inline-flex items-center gap-3 mb-8">
                  <span className="forr-hairline" />
                  <span className="forr-overline">{overline}</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  data-testid={`application-${pageKey}-headline`}
                  className="font-display forr-text-ink text-[36px] sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.04] font-medium tracking-[-0.03em]"
                >
                  {headline}
                  {headlineAccent && (
                    <>
                      {" "}
                      <span className="forr-text-teal-deep">
                        {headlineAccent}
                      </span>
                    </>
                  )}
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-2xl text-base sm:text-lg font-light forr-text-soft leading-relaxed">
                  {lead}
                </p>
              </Reveal>
            </div>

            <div className="col-span-12 lg:col-span-5 flex justify-center lg:justify-end">
              <Reveal delay={0.1} className="w-full max-w-[350px]">
                <AdaptiveLoopHeader
                  testId={`application-${pageKey}-loop`}
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* The Adaptive Challenge */}
      <section
        data-testid={`application-${pageKey}-challenge`}
        className="relative py-24 sm:py-32 forr-canvas-alt"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <SectionOverline
                  number="01"
                  label="The Adaptive Challenge"
                  testId={`application-${pageKey}-challenge-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  {challenge.title}
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8 lg:pl-8">
              <div className="space-y-6">
                {challenge.paragraphs.map((p, i) => (
                  <Reveal delay={0.1 + i * 0.05} key={i}>
                    <p className="text-lg font-light forr-text-soft leading-relaxed">
                      {p}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Traditional Gap */}
      <section
        data-testid={`application-${pageKey}-gap`}
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <SectionOverline
                  number="02"
                  label="The Traditional Gap"
                  testId={`application-${pageKey}-gap-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  {traditionalGap.title}
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8 lg:pl-8">
              <div
                className="rounded-[24px] p-8 sm:p-10 forr-surface"
                style={{
                  border: "1px solid var(--forr-line)",
                  boxShadow: "var(--forr-shadow-sm)",
                }}
              >
                {traditionalGap.paragraphs.map((p, i) => (
                  <Reveal delay={0.1 + i * 0.05} key={i}>
                    <p
                      className={`text-lg font-light forr-text-soft leading-relaxed ${
                        i > 0 ? "mt-5" : ""
                      }`}
                    >
                      {p}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Adaptive Physiology Perspective */}
      <section
        data-testid={`application-${pageKey}-perspective`}
        className="relative py-24 sm:py-32 forr-canvas-alt border-t border-line-soft overflow-hidden"
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] forr-radial-teal opacity-60 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="03"
              label="The Adaptive Physiology Perspective"
              testId={`application-${pageKey}-perspective-overline`}
            />
          </Reveal>
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  {perspective.title}
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8 mt-8 lg:mt-8">
              <div className="space-y-6">
                {perspective.paragraphs.map((p, i) => (
                  <Reveal delay={0.1 + i * 0.05} key={i}>
                    <p className="text-lg font-light forr-text-soft leading-relaxed">
                      {p}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Application */}
      <section
        data-testid={`application-${pageKey}-framework`}
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="04"
              label="Framework Application"
              testId={`application-${pageKey}-framework-overline`}
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              How adaptive intervention is applied here.
            </h2>
          </Reveal>

          <div className="mt-16 grid sm:grid-cols-2 gap-6">
            {frameworkApplication.map((step, i) => (
              <Reveal delay={i * 0.06} key={step.title}>
                <div
                  data-testid={`application-${pageKey}-framework-step-${i + 1}`}
                  className="forr-card rounded-[24px] p-8 h-full"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-display forr-text-teal-deep text-2xl font-medium tracking-tight">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="forr-overline">
                      {["Assess", "Prescribe", "Reinforce", "Retrain"][i]}
                    </span>
                  </div>
                  <h3 className="font-display forr-text-ink text-xl font-medium tracking-tight mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm font-light forr-text-soft leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Desired Capacities */}
      <section
        data-testid={`application-${pageKey}-capacities`}
        className="relative py-24 sm:py-32 forr-canvas-alt border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="05"
                  label="Desired Capacities"
                  testId={`application-${pageKey}-capacities-overline`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Capacities the loop tends to build.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-md text-base font-light forr-text-soft leading-relaxed">
                  Adaptive Physiology does not promise outcomes. It cultivates
                  capacities — the physiological and behavioral flexibility from
                  which better outcomes become possible.
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <div className="grid sm:grid-cols-2 gap-4">
                {capacities.map((c, i) => (
                  <Reveal delay={i * 0.05} key={c.label}>
                    <div
                      data-testid={`application-${pageKey}-capacity-${i + 1}`}
                      className="flex items-start gap-4 p-5 rounded-[16px] forr-surface"
                      style={{ border: "1px solid var(--forr-line-soft)" }}
                    >
                      <div
                        className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center"
                        style={{
                          backgroundColor: "var(--forr-teal-tint)",
                          color: "var(--forr-teal-deep)",
                        }}
                      >
                        {c.icon ? (
                          <c.icon size={16} strokeWidth={1.6} />
                        ) : (
                          <Check size={16} strokeWidth={1.6} />
                        )}
                      </div>
                      <div>
                        <div className="font-display forr-text-ink text-sm font-medium tracking-tight">
                          {c.label}
                        </div>
                        {c.body && (
                          <div className="mt-1 text-[13px] forr-text-mute font-light leading-relaxed">
                            {c.body}
                          </div>
                        )}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Exploration */}
      <section
        data-testid={`application-${pageKey}-related`}
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="06"
              label="Related Exploration"
              testId={`application-${pageKey}-related-overline`}
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              Continue through the methodology.
            </h2>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((r) => (
              <Reveal key={r.to}>
                <Link
                  to={r.to}
                  data-testid={`application-${pageKey}-related-${r.to.split("/").pop()}`}
                  className="forr-card rounded-[20px] p-6 block h-full group"
                >
                  <div className="text-[11px] uppercase tracking-[0.24em] forr-text-mute mb-3">
                    Explore
                  </div>
                  <div className="font-display forr-text-ink text-lg font-medium tracking-tight">
                    {r.label}
                  </div>
                  <div className="mt-2 text-sm forr-text-mute font-light leading-relaxed">
                    {r.desc}
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 forr-text-teal-deep text-sm">
                    Open
                    <ArrowRight
                      size={14}
                      strokeWidth={1.75}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <NextStep
        overline={nextStep?.overline || "Continue"}
        title={nextStep?.title || "Take the Adaptive Assessment"}
        description={
          nextStep?.description ||
          "The Adaptive Assessment is available now and provides a personalized read of your current nervous-system pattern inside the framework."
        }
        to={nextStep?.to || "/assessment/start"}
        cta={nextStep?.cta || "Take the Assessment"}
        testId={`application-${pageKey}-next`}
      />
    </PageShell>
  );
};

export default ApplicationTemplate;
